
  import { Component, Watch } from 'vue-property-decorator'
  import BasePanel from '@/components/forms/inspection/base/BasePanel.vue'
  import { Debounce } from '@/utils/decorators'
  import GFormSlot from '@/components/forms/GFormSlot.vue'
  import GFiles from '@/components/core/files/GFiles.vue'
  import { Component as Components } from '@/entities/vehicle'
  import { plainToInstance } from 'class-transformer'
  import { parseToNumber, replaceText, sendNotification } from '@/utils/general'
  import { DealAutoAttribute, Maintenance } from '@/entities/public'
  import MaintenancePanelExpanded from '@/components/forms/inspection/MaintenancePanelExpanded.vue'
  import _ from 'lodash'
  import RatingCell from '@/components/dataTables/cell/RatingCell.vue'
  import GCostField from '@/components/core/input/GCostField.vue'

@Component({
  methods: { replaceText },
  components: { GCostField, RatingCell, MaintenancePanelExpanded, GFiles, GFormSlot, BasePanel },
})
  export default class MaintenancePanel extends BasePanel {
  declare $refs: {
    form: HTMLFormElement,
    prepaid: MaintenancePanelExpanded[],
    done: MaintenancePanelExpanded[]
  };

  components: Components[] = []
  maintenances = []
  maintenanceBackupTypes = []
  prepaidMaintenances = []
  idProcessMaintenance = null
  isMaintenanceMissing = true
  maintenancePhoto = {
    properties: null,
  }

  maintenanceStatus = {
    done: null,
    prepaid: null,
  }

  maxPrepaidMaintenances = 0
  componentKeys = ['maintenance']
  mileage: DealAutoAttribute = plainToInstance(DealAutoAttribute, {})

  formData = {
    maintenance: {
      maxMaintenanceDone: {
        maintenance: null,
        parameter: null,
      },
      maxMaintenancePrepaid: {
        maintenance: null,
        parameter: null,
      },
      cost: null,
    },
  }

  fields = {
    maintenance: {
      items: [],
    },
    prepaidMaintenance: {
      items: [],
    },
  }

  errorIdentifierMessage = {
    maintenance: '',
  }

  get maintenance () {
    return this.findComponentBySlug(this.components, 'maintenance')
  }

  get questionMaintenanceDone () {
    const { maintenance } = this

    return maintenance?.findQuestion(1)
  }

  get questionMaintenancePrepaid () {
    const { maintenance } = this

    return maintenance?.findQuestion(2)
  }

  get allComponents () {
    const { selfPanel } = this
    return {
      maintenance: this.maintenance,
      maintenancePhoto: this.maintenancePhoto,
      selfPanel,
    }
  }

  get isCost () {
    const { maintenance, inspection } = this
    return {
      maintenance, inspection,
    }
  }

  validateAll () {
    const refs = ['done', 'prepaid']

    refs.forEach(ref => {
      this.$refs?.[ref]?.forEach(ref => {
        if (typeof ref.validate === 'function') {
          ref.validate()
        }
      })
    })
  }

  async send () {
    const { inspection, maintenance: component, questionMaintenanceDone, questionMaintenancePrepaid, disabledSend } = this

    if (disabledSend) {
      this.validateAll()
      sendNotification('Tiene mantenciones por validar', 'error')
      return
    }

    const maintenance = this.findClosestBackupMileage(inspection?.maintenances)
    const prepaidMaintenance = this.findClosestBackupMileage(inspection?.prepaidMaintenances)
    this.formData.maintenance.maxMaintenanceDone.maintenance = maintenance || this.findMaintenanceResponse(inspection?.maintenances)
    this.formData.maintenance.maxMaintenanceDone.parameter = questionMaintenanceDone || null
    this.formData.maintenance.maxMaintenancePrepaid.maintenance = prepaidMaintenance || this.findMaintenanceResponse(inspection?.prepaidMaintenances)
    this.formData.maintenance.maxMaintenancePrepaid.parameter = questionMaintenancePrepaid || null
    await this.sendMaintenance(component, this.formData.maintenance, inspection.id)
    this.step = this.step + 1
  }

  findMaintenanceResponse (maintenances) {
    const isResponse = maintenances?.find(maintenance => maintenance?.backupType?.name && maintenance.backupType.name === 'no_backup')
    if (!isResponse) {
      return null
    }
    return plainToInstance(Maintenance, { type: { mileage: 0 } })
  }

  @Watch('step', { immediate: true })
  async onStepChange (val) {
    const { componentKeys, inspection, categoryName } = this
    if (val > componentKeys.length) {
      const cost = this.formData.maintenance?.cost || 0
      await this.updateCategoryStatus(inspection, categoryName, cost, false, null, 3)
      this.$emit('inspection-category-done')
    }
  }

  @Watch('allComponents', { immediate: true, deep: true })
  @Debounce(500)
  async onComponentsChange (val) {
    const { idProcess, inspection, componentKeys, autoClose, disabled, selfPanel, displayQualification } = this

    if (!selfPanel) return

    const handleComponentChange = async (component, step, key) => {
      const matchingInspectedComponent = inspection.inspectedComponents?.find(
        ic => ic.inspectionComponent.id === component?.inspectionComponent?.id
      )

      if (matchingInspectedComponent) {
        if (!autoClose && !disabled && !displayQualification) {
          this.step = step
        }

        this.formData[key].photo = await this.fetchData({
          query: { name: 'find', model: 'FileProcess' },
          filter: {
            _and: [
              { id_process_record: { _eq: matchingInspectedComponent.id } },
              { parameter: { process: { id: { _eq: idProcess } } } },
              { parameter: { file_type: { name: { _eq: 'photo' } } } },
            ],
          },
          force: true,
        })
        const value = matchingInspectedComponent.componentQualificationValue
        const idAnswer = matchingInspectedComponent.componentQualification?.[0]?.id

        this.formData[key].answer = value?.[0] || idAnswer
      }
    }

    for (const [index, key] of componentKeys.entries()) {
      await handleComponentChange(val[key], index + 1, key)
    }
  }

  async mounted () {
    const process = await this.fetchData({
      query: { name: 'find', model: 'Process' },
      filter: { table_name: { _eq: 'maintenance' } },
    })

    const { category } = this
    this.maxPrepaidMaintenances = process[0].config.maxPrepaidMaintenances

    this.components = category.components

    this.maintenanceStatus.done = await this.fetchData({
      query: { name: 'find', model: 'ProcessStatus' },
      filter: { status: { name: { _eq: 'done' } } },
    })

    this.maintenanceStatus.prepaid = await this.fetchData({
      query: { name: 'find', model: 'ProcessStatus' },
      filter: { status: { name: { _eq: 'prepaid' } } },
    })

    const maintenanceProcess = await this.fetchData({
      query: { name: 'find', model: 'Process' },
      filter: { table_name: { _eq: 'maintenance' } },
    })

    this.idProcessMaintenance = maintenanceProcess?.[0]?.id

    this.maintenanceBackupTypes = await this.fetchData({
      query: { name: 'find', model: 'MaintenanceBackupType' },
      filter: {},
    })

    const fileInfo = await this.fetchData({
      query: { name: 'find', model: 'FileParameter' },
      filter: { process: { table_name: { _eq: 'maintenance' } } },
    })

    this.maintenancePhoto.properties = {
      accept: fileInfo[0].fileType.mimes,
      multiple: fileInfo[0].multiple,
      fileTypeId: fileInfo[0].fileType.id,
      name: fileInfo[0].name,

    }
  }

  get inspectionData () {
    const { inspection, idProcessInspection, selfPanel } = this

    return {
      inspection,
      idProcessInspection,
      selfPanel,
    }
  }

  @Watch('inspectionData', { immediate: true, deep: true })
  @Debounce(500)
  async onInspectionLoad (val) {
    const { inspection, idProcessInspection } = val

    this.fields.maintenance.items = []
    this.fields.prepaidMaintenance.items = []
    this.maintenances = []
    this.prepaidMaintenances = []
    if (!inspection?.id || !idProcessInspection) return
    const filter = {
      _and: [
        { inspection_component: { component: { slug: { _eq: 'mileage' } } } },
        { id_inspection: { _eq: inspection.id } },
      ],
    }

    const inspectedComponent = await this.fetchData({
      query: { name: 'find', model: 'InspectionInspectedComponent' },
      filter,
      force: true,
    })

    if (inspectedComponent?.length) {
      const dealAutoAttribute = await this.getDealAutoAttribute(inspectedComponent?.inspectionComponent?.component?.[0]?.id, idProcessInspection, inspection.id)
      this.mileage = dealAutoAttribute.find(da => da?.component?.slug === 'mileage')

      const allMaintenances = _.cloneDeep(val.inspection.allMaintenances)
      if (allMaintenances?.length) {
        this.findClosestMileage(this.mileage.value, allMaintenances)
      }
      await this.findMaintenanceType(this.mileage?.value)
    }

    if (inspection?.maintenances?.length) {
      this.maintenances = inspection?.maintenances
    }
    if (inspection?.prepaidMaintenances?.length) {
      this.fields.maintenance.items = this.fields.maintenance.items.filter(item => !inspection?.prepaidMaintenances?.some(pmt => pmt?.type.id === item.id))
      this.prepaidMaintenances = inspection?.prepaidMaintenances
    }

    await this.findPrepaidMaintenanceType(inspection?.maintenances?.[0]?.type?.mileage)
  }

  async findMaintenanceType (km) {
    if (!km) {
      this.fields.maintenance.items = []
      return
    }
    const { maintenances, inspection } = this

    const arrayMaintenances = maintenances?.length ? _.cloneDeep(maintenances) : _.cloneDeep(inspection?.maintenances)

    let max = { mileage: 0 }

    if (arrayMaintenances?.length) {
      max = arrayMaintenances.reduce((prev, current) => {
        return (prev.mileage > current.mileage) ? prev : current
      })
    }

    const search = max?.mileage > Number(km) ? max?.mileage : km

    const items = await this.fetchData({
      query: { name: 'find', model: 'MaintenanceType' },
      filter: { mileage: { _lte: search } },
    })

    this.fields.maintenance.items = items
    await this.findPrepaidMaintenanceType(items?.[items.length - 1]?.mileage)
  }

  async findPrepaidMaintenanceType (mileage, type = 'maintenance') {
    const { maxPrepaidMaintenances } = this
    if (mileage >= maxPrepaidMaintenances) return
    let items
    if (type === 'maintenance') {
      items = await this.fetchData({
        query: { name: 'find', model: 'MaintenanceType' },
        filter: { mileage: { _gt: mileage, _lte: maxPrepaidMaintenances } },
      })
    } else {
      items = await this.fetchData({
        query: { name: 'find', model: 'MaintenanceType' },
        filter: {},
      })
    }
    const isNotBackup = this.prepaidMaintenances[0]?.backupType?.name === 'no_backup' ? this.prepaidMaintenances[0] : null

    this.fields.prepaidMaintenance.items = isNotBackup ? items.filter(item => item.mileage <= isNotBackup?.mileage) : items
  }

  get isMaintenancesItems () {
    const { fields: { maintenance: { items } } } = this

    return items?.length
  }

  get isMaintenancesPrepaidItems () {
    const { fields: { prepaidMaintenance: { items } } } = this

    return items?.length
  }

  get auto () {
    const { inspection } = this

    return inspection?.deal?.auto
  }

  findClosestMileage (value, maintenances) {
    const max = maintenances?.[0]
    const valueToCompare = parseToNumber(value) >= max.mileage ? parseToNumber(value) : max.mileage

    const maxMaintenance = maintenances.reverse()
      .find(maintenance =>
        parseToNumber(maintenance?.isMileage) >= parseToNumber(valueToCompare) &&
        maintenance?.backupType?.name &&
        maintenance.backupType.name !== 'no_backup'
      )

    this.isMaintenanceMissing = !maxMaintenance
  }

  findClosestBackupMileage (maintenances) {
    if (!maintenances?.length) return null

    return maintenances.filter(maintenance => maintenance?.backupType?.name && maintenance.backupType.name !== 'no_backup')?.[0]
  }

  @Watch('isCost', { immediate: true, deep: true })
  onMaintenanceChance (val) {
    const inspected = val.inspection?.inspectedComponents?.find(ic => ic.inspectionComponent.id === val?.maintenance?.inspectionComponent?.id)
    if (inspected) {
      this.formData.maintenance.cost = inspected.cost
    }
  }

  @Watch('autoClose', { immediate: true })
  onDisableChange (val) {
    if (val) {
      this.step = 1
    }
  }

  @Watch('isMaintenanceMissing', { immediate: true })
  onCostMaintenanceChange (val) {
    if (!val && this.formData.maintenance.cost) {
      this.formData.maintenance.cost = 0
    }
  }

  get disabledSend () {
    const { maintenances, prepaidMaintenances } = this

    const isMissingResponseDone = maintenances.filter(maintenance => !maintenance.backupType) || []
    const isMissingResponsePrepaid = prepaidMaintenances.filter(maintenance => !maintenance.backupType) || []

    return isMissingResponseDone.length || isMissingResponsePrepaid.length
  }
  }
